import React from 'react';
import { formatValue } from '../utils/formatters';

const ContactDetailModal = ({ isOpen, onClose, contact }) => {
    if (!isOpen || !contact) {
        return null;
    }

    // Get score color
    const getScoreColor = (score) => {
        if (score >= 7) return '#34d399'; // green
        if (score >= 5) return '#fbbf24'; // yellow
        if (score >= 3) return '#f97316'; // orange
        return '#f87171'; // red
    };

    // Get contact display name
    const getContactName = () => {
        if (contact.firstname || contact.lastname) {
            return `${contact.firstname || ''} ${contact.lastname || ''}`.trim() || 'Unnamed Contact';
        }
        if (contact.role) {
            return contact.role;
        }
        if (contact.email) {
            return contact.email;
        }
        if (contact.phone) {
            return contact.phone;
        }
        return 'Unnamed Contact';
    };

    const renderField = (label, value, isLink = false, isEmail = false) => {
        if (!value) return null;

        return (
            <div style={{ marginBottom: '16px' }}>
                <div style={{
                    fontSize: '12px',
                    color: '#909090',
                    textTransform: 'uppercase',
                    marginBottom: '6px',
                    fontWeight: '600',
                    letterSpacing: '0.5px'
                }}>
                    {label}
                </div>
                <div style={{
                    fontSize: '14px',
                    color: '#e0e0e0',
                    wordBreak: 'break-word'
                }}>
                    {isLink ? (
                        <a
                            href={isEmail ? `mailto:${value}` : value}
                            target={isEmail ? undefined : "_blank"}
                            rel={isEmail ? undefined : "noopener noreferrer"}
                            style={{
                                color: '#60a5fa',
                                textDecoration: 'none'
                            }}
                            onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                            onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
                        >
                            {value}
                        </a>
                    ) : (
                        formatValue(value)
                    )}
                </div>
            </div>
        );
    };

    return (
        <div
            style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(7, 7, 10, 0.92)',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                zIndex: 10000,
                padding: '24px'
            }}
            onClick={onClose}
        >
            <div
                style={{
                    position: 'relative',
                    width: '100%',
                    maxWidth: '800px',
                    maxHeight: '90vh',
                    backgroundColor: '#121212',
                    borderRadius: '10px',
                    boxShadow: '0 18px 60px rgba(0, 0, 0, 0.55)',
                    border: '1px solid rgba(100, 181, 246, 0.35)',
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column'
                }}
                onClick={(e) => e.stopPropagation()}
            >
                <button
                    onClick={onClose}
                    style={{
                        position: 'absolute',
                        top: '14px',
                        right: '14px',
                        backgroundColor: 'rgba(220, 53, 69, 0.92)',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '50%',
                        width: '42px',
                        height: '42px',
                        fontSize: '24px',
                        cursor: 'pointer',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        fontWeight: 'bold',
                        boxShadow: '0 8px 20px rgba(220, 53, 69, 0.45)',
                        transition: 'transform 0.2s ease, background-color 0.2s ease',
                        zIndex: 1
                    }}
                    onMouseEnter={(e) => {
                        e.target.style.backgroundColor = 'rgba(207, 46, 60, 0.95)';
                        e.target.style.transform = 'scale(1.05)';
                    }}
                    onMouseLeave={(e) => {
                        e.target.style.backgroundColor = 'rgba(220, 53, 69, 0.92)';
                        e.target.style.transform = 'scale(1)';
                    }}
                >
                    ×
                </button>
                <div
                    style={{
                        flex: 1,
                        overflow: 'auto',
                        backgroundColor: '#0c0c0c',
                        padding: '24px'
                    }}
                >
                    <h3
                        style={{
                            margin: '0 0 24px 0',
                            color: '#e0e0e0',
                            fontWeight: '600',
                            fontSize: '20px'
                        }}
                    >
                        Contact Details
                    </h3>

                    {/* Contact Name/Title */}
                    <div style={{
                        backgroundColor: '#181818',
                        border: '1px solid rgba(100, 181, 246, 0.12)',
                        borderRadius: '8px',
                        padding: '20px',
                        marginBottom: '20px'
                    }}>
                        <div style={{
                            fontSize: '18px',
                            fontWeight: '600',
                            color: '#e0e0e0',
                            marginBottom: '12px'
                        }}>
                            {getContactName()}
                        </div>
                        {contact.is_nonsensical && (
                            <div style={{
                                display: 'inline-block',
                                backgroundColor: 'rgba(239, 68, 68, 0.15)',
                                border: '1px solid rgba(239, 68, 68, 0.4)',
                                borderRadius: '6px',
                                padding: '6px 12px',
                                fontSize: '12px',
                                color: '#ef4444',
                                fontWeight: '600',
                                marginTop: '8px'
                            }}>
                                Nonsensical
                            </div>
                        )}
                    </div>

                    {/* Scores */}
                    {(contact.completeness_score !== undefined || 
                      contact.reachability_score !== undefined || 
                      contact.relevance_score !== undefined || 
                      contact.overall_score !== undefined) && (
                        <div style={{
                            backgroundColor: '#181818',
                            border: '1px solid rgba(100, 181, 246, 0.12)',
                            borderRadius: '8px',
                            padding: '20px',
                            marginBottom: '20px'
                        }}>
                            <div style={{
                                fontSize: '14px',
                                color: '#d0d0d0',
                                fontWeight: '600',
                                marginBottom: '16px',
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px'
                            }}>
                                Scores
                            </div>
                            <div style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                                gap: '12px'
                            }}>
                                {contact.completeness_score !== undefined && contact.completeness_score !== null && (
                                    <div style={{
                                        backgroundColor: '#0c0c0c',
                                        border: '1px solid rgba(100, 181, 246, 0.08)',
                                        borderRadius: '6px',
                                        padding: '12px',
                                        textAlign: 'center'
                                    }}>
                                        <div style={{
                                            fontSize: '11px',
                                            color: '#909090',
                                            textTransform: 'uppercase',
                                            marginBottom: '6px',
                                            fontWeight: '600',
                                            letterSpacing: '0.5px'
                                        }}>
                                            Completeness
                                        </div>
                                        <div style={{
                                            fontSize: '20px',
                                            color: getScoreColor(contact.completeness_score),
                                            fontWeight: '700'
                                        }}>
                                            {formatValue(contact.completeness_score)}
                                        </div>
                                    </div>
                                )}
                                {contact.reachability_score !== undefined && contact.reachability_score !== null && (
                                    <div style={{
                                        backgroundColor: '#0c0c0c',
                                        border: '1px solid rgba(100, 181, 246, 0.08)',
                                        borderRadius: '6px',
                                        padding: '12px',
                                        textAlign: 'center'
                                    }}>
                                        <div style={{
                                            fontSize: '11px',
                                            color: '#909090',
                                            textTransform: 'uppercase',
                                            marginBottom: '6px',
                                            fontWeight: '600',
                                            letterSpacing: '0.5px'
                                        }}>
                                            Reachability
                                        </div>
                                        <div style={{
                                            fontSize: '20px',
                                            color: getScoreColor(contact.reachability_score),
                                            fontWeight: '700'
                                        }}>
                                            {formatValue(contact.reachability_score)}
                                        </div>
                                    </div>
                                )}
                                {contact.relevance_score !== undefined && contact.relevance_score !== null && (
                                    <div style={{
                                        backgroundColor: '#0c0c0c',
                                        border: '1px solid rgba(100, 181, 246, 0.08)',
                                        borderRadius: '6px',
                                        padding: '12px',
                                        textAlign: 'center'
                                    }}>
                                        <div style={{
                                            fontSize: '11px',
                                            color: '#909090',
                                            textTransform: 'uppercase',
                                            marginBottom: '6px',
                                            fontWeight: '600',
                                            letterSpacing: '0.5px'
                                        }}>
                                            Relevance
                                        </div>
                                        <div style={{
                                            fontSize: '20px',
                                            color: getScoreColor(contact.relevance_score),
                                            fontWeight: '700'
                                        }}>
                                            {formatValue(contact.relevance_score)}
                                        </div>
                                    </div>
                                )}
                                {contact.overall_score !== undefined && contact.overall_score !== null && (
                                    <div style={{
                                        backgroundColor: '#0c0c0c',
                                        border: `2px solid ${getScoreColor(contact.overall_score)}`,
                                        borderRadius: '6px',
                                        padding: '12px',
                                        textAlign: 'center'
                                    }}>
                                        <div style={{
                                            fontSize: '11px',
                                            color: '#909090',
                                            textTransform: 'uppercase',
                                            marginBottom: '6px',
                                            fontWeight: '600',
                                            letterSpacing: '0.5px'
                                        }}>
                                            Overall Score
                                        </div>
                                        <div style={{
                                            fontSize: '24px',
                                            color: getScoreColor(contact.overall_score),
                                            fontWeight: '700'
                                        }}>
                                            {formatValue(contact.overall_score)}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Contact Information */}
                    <div style={{
                        backgroundColor: '#181818',
                        border: '1px solid rgba(100, 181, 246, 0.12)',
                        borderRadius: '8px',
                        padding: '20px',
                        marginBottom: '20px'
                    }}>
                        <div style={{
                            fontSize: '14px',
                            color: '#d0d0d0',
                            fontWeight: '600',
                            marginBottom: '16px',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px'
                        }}>
                            Contact Information
                        </div>
                        {renderField('Salutation', contact.salutation)}
                        {renderField('First Name', contact.firstname)}
                        {renderField('Last Name', contact.lastname)}
                        {renderField('Role', contact.role)}
                        {renderField('Email', contact.email, true, true)}
                        {renderField('Phone', contact.phone)}
                        {renderField('WhatsApp', contact.whatsapp)}
                        {renderField('LinkedIn', contact.linkedin, true)}
                        {renderField('Website', contact.website, true)}
                        {renderField('Domain', contact.domain)}
                        {renderField('Postal Address', contact.postal_address)}
                        {renderField('Other Contact Info', contact.other_contact_info)}
                    </div>

                    {/* Source Information */}
                    <div style={{
                        backgroundColor: '#181818',
                        border: '1px solid rgba(100, 181, 246, 0.12)',
                        borderRadius: '8px',
                        padding: '20px',
                        marginBottom: '20px'
                    }}>
                        <div style={{
                            fontSize: '14px',
                            color: '#d0d0d0',
                            fontWeight: '600',
                            marginBottom: '16px',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px'
                        }}>
                            Source Information
                        </div>
                        {renderField('Source URL', contact.source_url, true)}
                        {renderField('Source Context', contact.source_context)}
                        {contact.source_urls && Array.isArray(contact.source_urls) && contact.source_urls.length > 0 && (
                            <div style={{ marginBottom: '16px' }}>
                                <div style={{
                                    fontSize: '12px',
                                    color: '#909090',
                                    textTransform: 'uppercase',
                                    marginBottom: '6px',
                                    fontWeight: '600',
                                    letterSpacing: '0.5px'
                                }}>
                                    Source URLs ({contact.source_urls.length})
                                </div>
                                <div style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: '4px'
                                }}>
                                    {contact.source_urls.map((url, index) => (
                                        <a
                                            key={index}
                                            href={url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            style={{
                                                fontSize: '13px',
                                                color: '#60a5fa',
                                                textDecoration: 'none',
                                                wordBreak: 'break-all'
                                            }}
                                            onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                                            onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
                                        >
                                            {url}
                                        </a>
                                    ))}
                                </div>
                            </div>
                        )}
                        {contact.source_contexts && Array.isArray(contact.source_contexts) && contact.source_contexts.length > 0 && (
                            <div style={{ marginBottom: '16px' }}>
                                <div style={{
                                    fontSize: '12px',
                                    color: '#909090',
                                    textTransform: 'uppercase',
                                    marginBottom: '6px',
                                    fontWeight: '600',
                                    letterSpacing: '0.5px'
                                }}>
                                    Source Contexts ({contact.source_contexts.length})
                                </div>
                                <ul style={{
                                    margin: 0,
                                    paddingLeft: '20px',
                                    fontSize: '13px',
                                    color: '#e0e0e0',
                                    lineHeight: '1.6'
                                }}>
                                    {contact.source_contexts.map((context, index) => (
                                        <li key={index} style={{ marginBottom: '4px' }}>
                                            {formatValue(context)}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </div>

                    {/* Rating Reasoning */}
                    {contact.rating_reasoning && (
                        <div style={{
                            backgroundColor: '#181818',
                            border: '1px solid rgba(100, 181, 246, 0.12)',
                            borderRadius: '8px',
                            padding: '20px'
                        }}>
                            <div style={{
                                fontSize: '14px',
                                color: '#d0d0d0',
                                fontWeight: '600',
                                marginBottom: '12px',
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px'
                            }}>
                                Rating Reasoning
                            </div>
                            <div style={{
                                fontSize: '13px',
                                color: '#d0d0d0',
                                lineHeight: '1.7',
                                whiteSpace: 'pre-wrap'
                            }}>
                                {formatValue(contact.rating_reasoning)}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ContactDetailModal;

